import { Button } from '@akin-home/buttons/native';
import { useHabitatID } from '@akin-home/habitats/react';
import { useListMealsByHabitat } from '@akin-home/meals/react';
import { Text } from '@akin-home/text/native';
import { colors } from '@akin-home/theme';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { endOfDay, startOfDay } from 'date-fns';
import { useMemo, useState } from 'react';
import { LayoutChangeEvent } from 'react-native';
import Carousel from 'react-native-reanimated-carousel';
import styled from 'styled-components/native';
import FullScreenLoader from '../../components/FullScreenLoader';
import { IndicatorDots } from '../../components/IndicatorDots';
import Spacer from '../../components/Spacer';
import { useMainDrawerNavigation } from '../../navigation/hooks';
import useCurrentDay from '../../utils/useCurrentDay';
import DashboardPanel from '../DashboardPanel';
import { DashboardMealItem } from './DashboardMealItem';
import { SmallDashboardMealItem } from './SmallDashboardMealItem';

interface Props {
  small?: boolean;
}

export const DashboardMealsPanel = ({ small }: Props) => {
  const [carousel_width, setCarouselWidth] = useState(0);
  const [meal_index, setMealIndex] = useState(0);
  const { navigate } = useMainDrawerNavigation();

  const habitatID = useHabitatID();
  const day = useCurrentDay();

  const [start, end] = useMemo(() => {
    const date = new Date(day);
    return [startOfDay(date).toISOString(), endOfDay(date).toISOString()];
  }, [day]);

  const { data, isLoading } = useListMealsByHabitat({
    habitatID,
    start: { between: [start, end] },
  });

  const meal = data?.[meal_index];

  const onLayout = ({
    nativeEvent: {
      layout: { width },
    },
  }: LayoutChangeEvent) => {
    setCarouselWidth(width);
  };

  const onPressChange = () => {
    meal &&
      navigate('RecipeSelect', { meal_id: meal.id, meal_type: meal.mealType });
  };

  const onPressCreateMealPlan = () => navigate('Meals');

  const onPressSeeRecipe = () => {
    const recipe_id = meal?.recipes?.[0]?.id;

    recipe_id &&
      navigate('RecipeDetails', {
        recipe_id,
      });
  };

  if (!isLoading && !data?.length) {
    return (
      <PlaceholderPanel>
        <PlaceholderTitle>
          <FontAwesomeIcon
            icon="salad"
            color={colors.orange.crayola}
            size={25}
          />
          <Text variant="h3" weight="semibold">
            Meals
          </Text>
        </PlaceholderTitle>

        <Button
          variant="secondary"
          icon="plus"
          size="large"
          onPress={onPressCreateMealPlan}
        >
          Create your meal plan
        </Button>
      </PlaceholderPanel>
    );
  }

  return (
    <Panel>
      {data?.length && !small ? (
        <Top>
          <IndicatorDots count={data.length} active_index={meal_index} />
        </Top>
      ) : (
        <Spacer height="16px" />
      )}

      <Center onLayout={onLayout}>
        {isLoading || carousel_width === 0 ? (
          <FullScreenLoader />
        ) : (
          <Carousel
            data={data}
            width={carousel_width}
            renderItem={({ item }) =>
              small ? (
                <SmallDashboardMealItem meal={item} />
              ) : (
                <DashboardMealItem meal={item} />
              )
            }
            onSnapToItem={setMealIndex}
          />
        )}
      </Center>

      {data?.length && small ? (
        <TopIndicatorDots count={data.length} active_index={meal_index} />
      ) : null}

      <Buttons>
        <BottomButton
          icon={['far', 'exchange']}
          variant="text"
          onPress={onPressChange}
        >
          Change
        </BottomButton>
        <BottomButton variant="secondary" onPress={onPressSeeRecipe}>
          See recipe
        </BottomButton>
      </Buttons>
    </Panel>
  );
};

const Panel = styled(DashboardPanel)`
  flex: 1;
`;

const PlaceholderPanel = styled(DashboardPanel)`
  background-color: ${colors.orange.background}
  flex: 1;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const PlaceholderTitle = styled.View`
  flex-direction: row;
  gap: 14px;
  justify-content: center;
  align-items: center;
`;

const Top = styled.View`
  flex-direction: row;
  justify-content: flex-end;
  padding: 16px;
  padding-bottom: 0;
`;

const TopIndicatorDots = styled(IndicatorDots)`
  position: absolute;
  top: 25px;
  right: 25px;
  background-color: white;
`;

const Center = styled.View`
  flex: 1;
  width: 100%;
`;

const Buttons = styled.View`
  flex-direction: row;
  align-items: center;
  padding: 16px;
`;

const BottomButton = styled(Button)`
  flex: 1;
  gap: 8px;
`;
